/**
 * Transport Vehicles — Admin Registry
 *
 * Combined staff-personal + fleet(house) + participant vehicle registry.
 *   GET    /api/transport/fleet-vehicles     (house/company vehicles, enriched with house_name/participant_name)
 *   GET    /api/transport/staff-vehicles     (enriched with staff_name)
 *   POST   /api/transport/staff-vehicles     (+ ?target_staff_id=)
 *   PUT/DELETE /api/transport/staff-vehicles/{id}
 *   POST   /api/vehicles                      (house/fleet + participant vehicles)
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Truck, Car, Plus, Pencil, Trash2, AlertTriangle, Loader2, User } from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { IconButton } from '@/components/common/IconButton';
import { toast } from 'sonner';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { participantLabel, staffLabel, PARTICIPANT_SEARCH_HINT, STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';

const EMPTY_STAFF_FORM = {
  label: '', make_model: '', registration: '', fuel_type: 'petrol',
  ndis_rate_per_km_override: '', reimbursement_rate_per_km_override: '',
  active: true, target_staff_id: '',
};
const EMPTY_HOUSE_FORM = { name: '', registration: '', model: '', rego_expiry: '', house_id: '', participant_id: '' };

function FeatureDisabledCard({ error }) {
  return (
    <Card data-testid="transport-feature-disabled">
      <CardContent className="pt-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            The Vehicle Log / Transport module is currently disabled.
            Ask a Super Admin to enable <code>transport_logging_enabled</code> in
            Feature Flags. {error?.message ? <span className="block text-xs mt-1 text-muted-foreground">{error.message}</span> : null}
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}

const personName = (p) => `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.full_name || p.name || (p.id || p._id);

export default function TransportVehiclesAdminPage() {
  const [fleet, setFleet] = useState([]);
  const [staffVehicles, setStaffVehicles] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [houses, setHouses] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [saving, setSaving] = useState(false);

  // Staff vehicle dialog
  const [staffDialog, setStaffDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState(EMPTY_STAFF_FORM);

  // House vehicle dialog (shared for fleet + participant, distinguished by mode)
  const [houseDialog, setHouseDialog] = useState(false);
  const [houseMode, setHouseMode] = useState('fleet'); // 'fleet' | 'participant'
  const [editingHouse, setEditingHouse] = useState(null);
  const [houseForm, setHouseForm] = useState(EMPTY_HOUSE_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, s, u, h, p] = await Promise.all([
        api.get('/transport/fleet-vehicles').catch(e => e.response),
        api.get('/transport/staff-vehicles').catch(e => e.response),
        api.get('/admin/users').catch(() => ({ data: { data: [] } })),
        api.get('/houses?pageSize=100').catch(() => ({ data: { items: [] } })),
        api.get('/participants?pageSize=100').catch(() => ({ data: { items: [] } })),
      ]);
      const featureOff = (f?.status === 403 && f?.data?.error?.code === 'FEATURE_DISABLED') ||
                         (s?.status === 403 && s?.data?.error?.code === 'FEATURE_DISABLED');
      if (featureOff) {
        setFeatureDisabled(true);
      } else {
        setFleet(f?.data?.data || []);
        setStaffVehicles(s?.data?.data || []);
        setStaffUsers((u?.data?.data?.items || u?.data?.data || u?.data || []).filter(x => x.is_active !== false));
        setHouses(h?.data?.data?.items || h?.data?.items || h?.data?.data || []);
        setParticipants(p?.data?.data?.items || p?.data?.items || p?.data?.data || []);
      }
    } catch (err) {
      toast.error('Failed to load vehicles', { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const participantVehicles = fleet.filter(v => v.participant_id);
  const fleetOnly = fleet.filter(v => !v.participant_id);

  const staffOptions = useMemo(() => staffUsers.map(u => ({ value: u.id || u._id, label: u.name || u.full_name || u.email })), [staffUsers]);
  const houseOptions = useMemo(() => houses.map(h => ({ value: h.id || h._id, label: h.name || h.house_no || (h.id || h._id) })), [houses]);
  const participantOptions = useMemo(() => participants.map(p => ({ value: p.id || p._id, label: personName(p) })), [participants]);

  const staffNameById = useCallback((id) => {
    if (!id) return '—';
    const u = staffUsers.find(x => (x.id || x._id) === id);
    return u ? (u.name || u.full_name || u.email || id) : (id.slice(0, 8) + '…');
  }, [staffUsers]);
  const houseNameById = useCallback((id) => houses.find(h => (h.id || h._id) === id)?.name || id || '—', [houses]);

  // ── Staff vehicle CRUD ──
  const openStaffCreate = () => { setStaffForm(EMPTY_STAFF_FORM); setEditingStaff(null); setStaffDialog(true); };
  const openStaffEdit = (v) => {
    setStaffForm({
      label: v.label || '', make_model: v.make_model || '', registration: v.registration || '',
      fuel_type: v.fuel_type || 'petrol',
      ndis_rate_per_km_override: v.ndis_rate_per_km_override ?? '',
      reimbursement_rate_per_km_override: v.reimbursement_rate_per_km_override ?? '',
      active: v.active !== false, target_staff_id: v.staff_id || '',
    });
    setEditingStaff(v);
    setStaffDialog(true);
  };
  const saveStaff = async () => {
    setSaving(true);
    try {
      const { target_staff_id, ...rest } = staffForm;
      const payload = {
        ...rest,
        ndis_rate_per_km_override: rest.ndis_rate_per_km_override === '' ? null : Number(rest.ndis_rate_per_km_override),
        reimbursement_rate_per_km_override: rest.reimbursement_rate_per_km_override === '' ? null : Number(rest.reimbursement_rate_per_km_override),
      };
      if (editingStaff) {
        await api.put(`/transport/staff-vehicles/${editingStaff.id || editingStaff._id}`, payload);
        toast.success('Vehicle updated');
      } else {
        const url = target_staff_id
          ? `/transport/staff-vehicles?target_staff_id=${encodeURIComponent(target_staff_id)}`
          : '/transport/staff-vehicles';
        await api.post(url, payload);
        toast.success('Vehicle registered');
      }
      setStaffDialog(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.detail || {};
      toast.error('Failed to save', { description: detail.message || JSON.stringify(detail) });
    } finally { setSaving(false); }
  };
  const removeStaff = async (v) => {
    if (!window.confirm(`Soft-delete vehicle "${v.label || v.registration}"? Trip log references remain intact.`)) return;
    try {
      await api.delete(`/transport/staff-vehicles/${v.id || v._id}`);
      toast.success('Vehicle archived');
      await load();
    } catch (err) {
      toast.error('Failed to delete', { description: err?.response?.data?.error?.message });
    }
  };

  // ── House vehicle CRUD (fleet + participant) ──
  const openHouseCreate = (mode) => { setHouseMode(mode); setHouseForm(EMPTY_HOUSE_FORM); setEditingHouse(null); setHouseDialog(true); };
  const openHouseEdit = (v, mode) => {
    setHouseMode(mode);
    setHouseForm({
      name: v.name || v.label || '', registration: v.registration || '', model: v.model || v.make_model || '',
      rego_expiry: v.rego_expiry || '', house_id: v.house_id || '', participant_id: v.participant_id || '',
    });
    setEditingHouse(v);
    setHouseDialog(true);
  };
  const saveHouse = async () => {
    setSaving(true);
    try {
      const payload = {
        name: houseForm.name, registration: houseForm.registration, model: houseForm.model || undefined,
        rego_expiry: houseForm.rego_expiry || undefined,
        house_id: houseMode === 'participant' ? (houseForm.house_id || undefined) : houseForm.house_id,
        participant_id: houseMode === 'participant' ? (houseForm.participant_id || null) : null,
      };
      if (editingHouse) {
        await api.put(`/vehicles/${editingHouse.id || editingHouse._id}`, payload);
        toast.success('Vehicle updated');
      } else {
        await api.post('/vehicles', payload);
        toast.success(houseMode === 'participant' ? 'Participant vehicle added' : 'Fleet vehicle added');
      }
      setHouseDialog(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.detail || {};
      toast.error('Failed to save', { description: detail.message || JSON.stringify(detail) });
    } finally { setSaving(false); }
  };

  if (featureDisabled) {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <h1 className="text-2xl font-semibold">Transport · Vehicles</h1>
        <FeatureDisabledCard />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-4" data-testid="transport-vehicles-admin-page">
      <div>
        <h1 className="text-2xl font-semibold">Transport · Vehicles</h1>
        <p className="text-sm text-muted-foreground">
          Staff personal, fleet (house-owned) and participant vehicles for NDIS Activity-Based Transport.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="vehicles-summary">
        <div className="rounded-lg border bg-white p-3" data-testid="vehicles-summary-fleet">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Fleet vehicles</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{fleetOnly.length}</div>
        </div>
        <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3" data-testid="vehicles-summary-staff">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Staff personal</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-sky-700">{staffVehicles.length}</div>
        </div>
        <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="vehicles-summary-participant">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Participant</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-violet-700">{participantVehicles.length}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="vehicles-summary-active">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Staff active</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-emerald-700">{staffVehicles.filter(v => v.active !== false).length}</div>
        </div>
      </div>

      <Tabs defaultValue="staff">
        <TabsList>
          <TabsTrigger value="staff" data-testid="tab-staff-vehicles"><Car className="h-4 w-4 mr-1" />Staff personal ({staffVehicles.length})</TabsTrigger>
          <TabsTrigger value="fleet" data-testid="tab-fleet-vehicles"><Truck className="h-4 w-4 mr-1" />Fleet ({fleetOnly.length})</TabsTrigger>
          <TabsTrigger value="participant" data-testid="tab-participant-vehicles"><User className="h-4 w-4 mr-1" />Participant ({participantVehicles.length})</TabsTrigger>
        </TabsList>

        {/* Staff personal */}
        <TabsContent value="staff">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle className="text-base">Staff personal vehicles</CardTitle>
                <CardDescription>Drivers can claim NDIS km against approved trips on personal vehicles. Optional per-vehicle rate overrides.</CardDescription>
              </div>
              <Button size="sm" onClick={openStaffCreate} data-testid="register-staff-vehicle-btn"><Plus className="h-4 w-4 mr-1" />Register staff vehicle</Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <TableSkeleton rows={4} columns={9} testid="staff-vehicles-loading" />
              ) : staffVehicles.length === 0 ? (
                <EmptyState icon={Car} title="No staff vehicles registered" description="Add a vehicle to start tracking reimbursements."
                  action={<Button size="sm" onClick={openStaffCreate}><Plus className="h-4 w-4 mr-1" />Register staff vehicle</Button>} testid="staff-vehicles-empty" />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff</TableHead><TableHead>Label</TableHead><TableHead>Registration</TableHead>
                      <TableHead>Make/Model</TableHead><TableHead>Fuel</TableHead><TableHead>NDIS $/km</TableHead>
                      <TableHead>Reimb $/km</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staffVehicles.map(v => (
                      <TableRow key={v.id || v._id} data-testid={`staff-vehicle-row-${v.id || v._id}`}>
                        <TableCell className="text-sm" data-testid={`staff-vehicle-owner-${v.id || v._id}`}>{v.staff_name || staffNameById(v.staff_id)}</TableCell>
                        <TableCell className="font-medium">{v.label || '—'}</TableCell>
                        <TableCell className="font-mono text-xs">{v.registration}</TableCell>
                        <TableCell className="text-sm">{v.make_model || '—'}</TableCell>
                        <TableCell className="text-sm capitalize">{v.fuel_type || '—'}</TableCell>
                        <TableCell className="text-sm">{v.ndis_rate_per_km_override ?? '(org default)'}</TableCell>
                        <TableCell className="text-sm">{v.reimbursement_rate_per_km_override ?? '(org default)'}</TableCell>
                        <TableCell>{v.active === false ? <Badge variant="outline">Inactive</Badge> : <Badge>Active</Badge>}</TableCell>
                        <TableCell className="text-right">
                          <IconButton label="Edit vehicle" onClick={() => openStaffEdit(v)} testid={`edit-vehicle-${v.id || v._id}`}><Pencil className="h-3 w-3" /></IconButton>
                          <IconButton label="Delete vehicle" onClick={() => removeStaff(v)} testid={`delete-vehicle-${v.id || v._id}`}><Trash2 className="h-3 w-3" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fleet */}
        <TabsContent value="fleet">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle className="text-base">Fleet vehicles</CardTitle>
                <CardDescription>Company-owned house vehicles.</CardDescription>
              </div>
              <Button size="sm" onClick={() => openHouseCreate('fleet')} data-testid="add-fleet-vehicle-btn"><Plus className="h-4 w-4 mr-1" />Add fleet vehicle</Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <TableSkeleton rows={4} columns={6} testid="fleet-vehicles-loading" />
              ) : fleetOnly.length === 0 ? (
                <EmptyState icon={Truck} title="No fleet vehicles" description="Add a company-owned house vehicle."
                  action={<Button size="sm" onClick={() => openHouseCreate('fleet')}><Plus className="h-4 w-4 mr-1" />Add fleet vehicle</Button>} testid="fleet-vehicles-empty" />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead><TableHead>Registration</TableHead><TableHead>Model</TableHead>
                      <TableHead>House</TableHead><TableHead>Rego Expiry</TableHead><TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fleetOnly.map(v => (
                      <TableRow key={v.id || v._id} data-testid={`fleet-vehicle-row-${v.id || v._id}`}>
                        <TableCell className="font-medium">{v.name || v.label || '—'}</TableCell>
                        <TableCell className="font-mono text-xs">{v.registration}</TableCell>
                        <TableCell className="text-sm">{v.model || v.make_model || '—'}</TableCell>
                        <TableCell className="text-sm" data-testid={`fleet-vehicle-house-${v.id || v._id}`}>{v.house_name || houseNameById(v.house_id)}</TableCell>
                        <TableCell className="text-sm">{v.rego_expiry || '—'}</TableCell>
                        <TableCell className="text-right">
                          <IconButton label="Edit vehicle" onClick={() => openHouseEdit(v, 'fleet')} testid={`edit-fleet-vehicle-${v.id || v._id}`}><Pencil className="h-3 w-3" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Participant */}
        <TabsContent value="participant">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle className="text-base">Participant vehicles</CardTitle>
                <CardDescription>Vehicles that belong to a participant.</CardDescription>
              </div>
              <Button size="sm" onClick={() => openHouseCreate('participant')} data-testid="add-participant-vehicle-btn"><Plus className="h-4 w-4 mr-1" />Add participant vehicle</Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <TableSkeleton rows={4} columns={6} testid="participant-vehicles-loading" />
              ) : participantVehicles.length === 0 ? (
                <EmptyState icon={User} title="No participant vehicles" description="Add a vehicle that belongs to a participant."
                  action={<Button size="sm" onClick={() => openHouseCreate('participant')}><Plus className="h-4 w-4 mr-1" />Add participant vehicle</Button>} testid="participant-vehicles-empty" />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead><TableHead>Registration</TableHead><TableHead>Model</TableHead>
                      <TableHead>Participant</TableHead><TableHead>House</TableHead><TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {participantVehicles.map(v => (
                      <TableRow key={v.id || v._id} data-testid={`participant-vehicle-row-${v.id || v._id}`}>
                        <TableCell className="font-medium">{v.name || v.label || '—'}</TableCell>
                        <TableCell className="font-mono text-xs">{v.registration}</TableCell>
                        <TableCell className="text-sm">{v.model || v.make_model || '—'}</TableCell>
                        <TableCell className="text-sm" data-testid={`participant-vehicle-participant-${v.id || v._id}`}>{v.participant_name || '—'}</TableCell>
                        <TableCell className="text-sm">{v.house_name || houseNameById(v.house_id)}</TableCell>
                        <TableCell className="text-right">
                          <IconButton label="Edit vehicle" onClick={() => openHouseEdit(v, 'participant')} testid={`edit-participant-vehicle-${v.id || v._id}`}><Pencil className="h-3 w-3" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Staff vehicle dialog */}
      <Dialog open={staffDialog} onOpenChange={(o) => !o && setStaffDialog(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingStaff ? 'Edit staff vehicle' : 'Register staff vehicle'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Assign to staff *</Label>
              {editingStaff ? (
                <Input value={staffNameById(editingStaff.staff_id)} readOnly data-testid="vehicle-form-staff-readonly" className="bg-muted" />
              ) : (
                <AsyncSearchSelect
                  endpoint="/staff"
                  value={staffForm.target_staff_id}
                  onChange={(v) => setStaffForm({ ...staffForm, target_staff_id: v || '' })}
                  getOptionValue={(s) => s.user_id || s.id}
                  getOptionLabel={staffLabel}
                  selectedLabel={staffOptions.find(o => String(o.value) === String(staffForm.target_staff_id))?.label}
                  placeholder="— Register against myself —"
                  searchPlaceholder={STAFF_SEARCH_HINT}
                  emptyText="No staff match that search"
                  clearLabel="— Register against myself —"
                  testId="vehicle-form-staff"
                />
              )}
              <p className="text-xs text-muted-foreground mt-1">Pick the support worker who drives this vehicle so it appears in their mobile Log Trip dropdown.</p>
            </div>
            <div className="col-span-2"><Label>Label *</Label><Input value={staffForm.label} onChange={e => setStaffForm({ ...staffForm, label: e.target.value })} placeholder="My Toyota" data-testid="vehicle-form-label" /></div>
            <div><Label>Registration *</Label><Input value={staffForm.registration} onChange={e => setStaffForm({ ...staffForm, registration: e.target.value })} placeholder="ABC-123" data-testid="vehicle-form-registration" /></div>
            <div>
              <Label>Fuel</Label>
              <select className="w-full border rounded-md h-10 px-2 bg-background text-sm" value={staffForm.fuel_type} onChange={e => setStaffForm({ ...staffForm, fuel_type: e.target.value })} data-testid="vehicle-form-fuel">
                <option value="petrol">Petrol</option><option value="diesel">Diesel</option><option value="electric">Electric</option><option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div className="col-span-2"><Label>Make / Model</Label><Input value={staffForm.make_model} onChange={e => setStaffForm({ ...staffForm, make_model: e.target.value })} placeholder="Toyota Camry 2020" data-testid="vehicle-form-make-model" /></div>
            <div><Label>NDIS $/km override</Label><Input type="number" step="0.01" value={staffForm.ndis_rate_per_km_override} onChange={e => setStaffForm({ ...staffForm, ndis_rate_per_km_override: e.target.value })} placeholder="(org default)" data-testid="vehicle-form-ndis-rate" /></div>
            <div><Label>Reimb $/km override</Label><Input type="number" step="0.01" value={staffForm.reimbursement_rate_per_km_override} onChange={e => setStaffForm({ ...staffForm, reimbursement_rate_per_km_override: e.target.value })} placeholder="(org default)" data-testid="vehicle-form-reimb-rate" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStaffDialog(false)}>Cancel</Button>
            <Button onClick={saveStaff} disabled={saving || !staffForm.label || !staffForm.registration} data-testid="vehicle-form-save">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{editingStaff ? 'Save changes' : 'Register'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* House vehicle dialog (fleet + participant) */}
      <Dialog open={houseDialog} onOpenChange={(o) => !o && setHouseDialog(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingHouse ? 'Edit' : 'Add'} {houseMode === 'participant' ? 'participant vehicle' : 'fleet vehicle'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {houseMode === 'participant' ? (
              <div className="col-span-2">
                <Label>Participant name *</Label>
                <AsyncSearchSelect
                  endpoint="/participants"
                  value={houseForm.participant_id}
                  onChange={(v) => setHouseForm({ ...houseForm, participant_id: v })}
                  getOptionLabel={participantLabel}
                  selectedLabel={participantOptions.find(o => String(o.value) === String(houseForm.participant_id))?.label}
                  placeholder="Select participant"
                  searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                  emptyText="No participants match that search"
                  testId="house-vehicle-form-participant"
                />
              </div>
            ) : (
              <div className="col-span-2">
                <Label>House *</Label>
                <Select value={houseForm.house_id} onValueChange={(v) => setHouseForm({ ...houseForm, house_id: v })}>
                  <SelectTrigger data-testid="house-vehicle-form-house"><SelectValue placeholder="Select house" /></SelectTrigger>
                  <SelectContent>
                    {houseOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2"><Label>Vehicle name *</Label><Input value={houseForm.name} onChange={e => setHouseForm({ ...houseForm, name: e.target.value })} placeholder="House Van" data-testid="house-vehicle-form-name" /></div>
            <div><Label>Registration *</Label><Input value={houseForm.registration} onChange={e => setHouseForm({ ...houseForm, registration: e.target.value })} placeholder="ABC-123" data-testid="house-vehicle-form-registration" /></div>
            <div><Label>Registration Expiry</Label><Input type="date" value={houseForm.rego_expiry} onChange={e => setHouseForm({ ...houseForm, rego_expiry: e.target.value })} data-testid="house-vehicle-form-rego-expiry" /></div>
            <div className="col-span-2"><Label>Make / Model</Label><Input value={houseForm.model} onChange={e => setHouseForm({ ...houseForm, model: e.target.value })} placeholder="Toyota HiAce 2020" data-testid="house-vehicle-form-model" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHouseDialog(false)}>Cancel</Button>
            <Button onClick={saveHouse}
              disabled={saving || !houseForm.name || !houseForm.registration || (houseMode === 'participant' ? !houseForm.participant_id : !houseForm.house_id)}
              data-testid="house-vehicle-form-save">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{editingHouse ? 'Save changes' : 'Add vehicle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
